import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { motion } from 'framer-motion';
import { Loader2, Utensils } from 'lucide-react';
import { GOAL_CONFIG } from './constants';
import { getDifficultyLabel } from '../../utils/constants';

export const AllPlansView = ({ 
  allPlans, 
  recommendations, 
  followPlan, 
  viewPlanDetail,
  seedMealPlans,
  seeding 
}) => {
  if (allPlans.length === 0) {
    return (
      <div className="bg-card border border-border p-12 text-center">
        <Utensils className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
        <h3 className="font-heading text-xl text-white uppercase tracking-wider mb-2">
          No Meal Plans Available
        </h3>
        <p className="text-muted-foreground mb-6">
          Generate sample meal plans to get started
        </p>
        <Button
          onClick={seedMealPlans}
          disabled={seeding}
          className="bg-primary text-black hover:bg-primary/90"
        >
          {seeding ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
          Generate Meal Plans
        </Button>
      </div>
    );
  }

  return (
    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="plans-grid">
      {allPlans.map((plan, i) => (
        <PlanCard 
          key={plan.id}
          plan={plan}
          index={i}
          isActive={recommendations?.active_plan_id === plan.id}
          followPlan={followPlan}
          viewPlanDetail={viewPlanDetail}
        />
      ))}
    </div>
  );
};

const PlanCard = ({ plan, index, isActive, followPlan, viewPlanDetail }) => {
  const goalConfig = GOAL_CONFIG[plan.goal] || GOAL_CONFIG.general_fitness;
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.1 }}
      className={`bg-card border overflow-hidden ${
        isActive ? 'border-primary' : 'border-border'
      }`}
      data-testid={`plan-${plan.id}`}
    >
      {isActive && (
        <div className="bg-primary text-black text-center py-1 text-sm font-medium">
          Currently Following
        </div>
      )}
      
      <div className="p-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="font-heading text-xl text-white uppercase tracking-wider">
              {plan.name}
            </h3>
            <Badge className={`${goalConfig.bg} ${goalConfig.color} mt-2`}>
              {goalConfig.label}
            </Badge>
          </div>
          <div className="text-right">
            <div className="text-sm text-muted-foreground">{plan.duration_days} days</div>
            <div className="text-xs text-muted-foreground capitalize">{getDifficultyLabel(plan.difficulty)}</div>
          </div>
        </div>
        
        <p className="text-muted-foreground text-sm mb-4">{plan.description}</p>
        
        <div className="grid grid-cols-2 gap-2 text-sm mb-4">
          <div className="bg-white/5 p-2 text-center">
            <div className="text-white font-medium">{plan.daily_calories}</div>
            <div className="text-xs text-muted-foreground">Calories</div>
          </div>
          <div className="bg-white/5 p-2 text-center">
            <div className="text-white font-medium">{plan.daily_protein}g</div>
            <div className="text-xs text-muted-foreground">Protein</div>
          </div>
        </div>
        
        <div className="flex gap-2">
          <Button
            variant="outline"
            className="flex-1"
            onClick={() => viewPlanDetail(plan.id)}
          >
            View Details
          </Button>
          {!isActive && (
            <Button
              className="flex-1 bg-primary text-black hover:bg-primary/90"
              onClick={() => followPlan(plan.id)}
            >
              Start Plan
            </Button>
          )}
        </div>
      </div>
    </motion.div>
  );
};
